import { Vector3, Vector2, Raycaster, RingGeometry, MeshBasicMaterial, Mesh, DoubleSide } from "three";

/**
 * 地形笔刷工具类
 * 用于通过鼠标交互修改地形网格的高度
 */
export class TerrainBrush {
  constructor(manager) {
    this.manager = manager;
    this.enabled = false;
    this.isDrawing = false;
    
    // 笔刷参数
    this.radius = 1.0;        // 笔刷半径
    this.strength = 1.1;      // 笔刷强度
    //this.falloffType = 'linear'; // 衰减类型: 'linear', 'gaussian'
    this.falloffType='gaussian';
    this.invert = false;      // 是否反向（降低地形）
    
    // 射线拾取
    this.raycaster = new Raycaster();
    
    // 地形对象缓存
    this.terrainMeshes = [];
    
    // 鼠标状态
    this.lastMousePosition = new Vector2();
    this.currentMousePosition = new Vector2();
    
    // 性能优化：限制更新频率
    this.lastUpdateTime = 0;
    
    // 可视化指示器
    this.brushIndicator = null;
    this.indicatorMaterial = null;
    this.createBrushIndicator();

    this.modifyIntervalId = null;
    
    // 用于存储地形的初始状态
    this.originalTerrainStates = new Map(); // 使用Map存储多个地形的初始状态
  }

  /**
   * 创建笔刷可视化指示器
   */
  createBrushIndicator() {
    console.log("创建笔刷指示器，半径:", this.radius);
    
    // 创建环形几何体作为笔刷范围指示器
    const geometry = new RingGeometry(this.radius * 0.9, this.radius, 32);
    
    // 创建材质
    this.indicatorMaterial = new MeshBasicMaterial({
      color: this.invert ? 0xff4444 : 0x44ff44, // 红色下降，绿色抬升
      transparent: true,
      opacity: 0.5, // 增加透明度，使其更明显
      side: DoubleSide,
      depthWrite: false,
      depthTest: false // 禁用深度测试，确保始终可见
    });
    
    // 创建网格
    this.brushIndicator = new Mesh(geometry, this.indicatorMaterial);
    this.brushIndicator.rotation.x = -Math.PI / 2; // 水平放置
    this.brushIndicator.visible = false;
    this.brushIndicator.name = "TerrainBrushIndicator";
    this.brushIndicator.renderOrder = 999; // 设置高渲染顺序，确保在其他对象之上
    
    // 添加到场景
    if (this.manager.sceneManager && this.manager.sceneManager.scene) {
      console.log("将笔刷指示器添加到场景");
      this.manager.sceneManager.scene.add(this.brushIndicator);
    } else {
      console.error("无法添加笔刷指示器到场景，sceneManager或scene不存在");
    }
  }

  /**
   * 更新笔刷指示器
   */
  updateBrushIndicator(position) {
    if (!this.brushIndicator || !this.enabled) {
      return;
    }
    
    // 更新位置
    this.brushIndicator.position.copy(position);
    this.brushIndicator.position.y += 0.05; // 增加抬高高度，确保在地形上方
    
    // 更新大小
    const geometry = new RingGeometry(this.radius * 0.9, this.radius, 32);
    this.brushIndicator.geometry.dispose();
    this.brushIndicator.geometry = geometry;
    
    // 更新颜色
    this.indicatorMaterial.color.setHex(this.invert ? 0xff4444 : 0x44ff44);
    
    // 显示指示器
    this.brushIndicator.visible = true;
  }

  /**
   * 隐藏笔刷指示器
   */
  hideBrushIndicator() {
    if (this.brushIndicator) {
      this.brushIndicator.visible = false;
    }
  }

  /**
   * 启用地形笔刷
   */
  enable() {
    this.enabled = true;
    this.updateTerrainMeshes();
    
    console.log("地形笔刷已启用");
  }

  /**
   * 禁用地形笔刷
   */
  disable() {
    this.enabled = false;
    this.isDrawing = false;
    this.hideBrushIndicator();
    
    console.log("地形笔刷已禁用");
  }

  /**
   * 更新地形网格列表
   * 查找场景中名称包含"地形"的网格对象
   */
  updateTerrainMeshes() {
    this.terrainMeshes = [];
    
    if (!this.manager.sceneManager) {
      console.error("SceneManager不存在");
      return;
    }
    
    if (!this.manager.sceneManager.singleModels) {
      console.error("singleModels不存在");
      return;
    }

    // 查找地形对象
    this.manager.sceneManager.singleModels.forEach(model => {
      if (!model._delete && model.visible) {
        model.traverse(child => {
          if (child.isMesh) {
            // 放宽条件，不仅检查名称包含"地形"，还检查其他可能的地形对象
            const isTerrainLike = 
              (model.name && model.name.includes("地形")) || 
              (child.name && child.name.includes("地形")) ||
              (model.name && model.name.toLowerCase().includes("terrain")) ||
              (child.name && child.name.toLowerCase().includes("terrain")) ;
            
            if (isTerrainLike) {
              this.terrainMeshes.push(child);
            }
          }
        });
      }
    });

    // 如果没有找到地形，也包含默认地面
    /*if (this.manager.sceneManager.floor) {
      this.terrainMeshes.push(this.manager.sceneManager.floor);
    }

    // 如果仍然没有找到地形，尝试查找任何可能的平面网格
    if (this.terrainMeshes.length === 0) {
      this.manager.sceneManager.singleModels.forEach(model => {
        if (!model._delete && model.visible) {
          model.traverse(child => {
            if (child.isMesh && child.geometry) {
              this.terrainMeshes.push(child);
            }
          });
        }
      });
    }*/

  }

  /**
   * 设置笔刷参数
   */
  setRadius(radius) {
    this.radius = Math.max(0.1, Math.min(10, radius));
    // 如果指示器可见，更新其大小
    if (this.brushIndicator && this.brushIndicator.visible) {
      const geometry = new RingGeometry(this.radius * 0.9, this.radius, 32);
      this.brushIndicator.geometry.dispose();
      this.brushIndicator.geometry = geometry;
    }
  }

  setStrength(strength) {
    this.strength = Math.max(0.01, Math.min(2.0, strength));
  }

  setFalloffType(type) {
    if (['linear', 'gaussian'].includes(type)) {
      this.falloffType = type;
    }
  }

  setInvert(invert) {
    this.invert = invert;
    // 更新指示器颜色
    if (this.indicatorMaterial) {
      this.indicatorMaterial.color.setHex(this.invert ? 0xff4444 : 0x44ff44);
    }
  }

  setDirection(direction) {
    // direction: 1 为抬升，-1 为下降
    this.invert = direction < 0;
    // 更新指示器颜色
    if (this.indicatorMaterial) {
      this.indicatorMaterial.color.setHex(this.invert ? 0xff4444 : 0x44ff44);
    }
  }

  /**
   * 鼠标按下事件
   */
  onPointerDown(event) {
    if (!this.enabled) return;
    
    // 只有鼠标左键按下时才开始绘制
    if (event.button === 0) { // 0 表示鼠标左键
      this.isDrawing = true;
      this.updateMousePosition(event);
      this.lastMousePosition.copy(this.currentMousePosition);

      this.modifyIntervalId=setInterval(()=>{
        if(this.isDrawing){
          this.modifyTerrain(event);
        }
      },100);
    }
  }

  /**
   * 鼠标移动事件
   */
  onPointerMove(event) {
    if (!this.enabled) return;

    this.updateMousePosition(event);
    
    // 更新笔刷指示器位置
    this.updateBrushIndicatorPosition(event);
  }

  /**
   * 更新笔刷指示器位置
   */
  updateBrushIndicatorPosition(event) {
    
    if (this.terrainMeshes.length === 0) {
      this.updateTerrainMeshes();
      if (this.terrainMeshes.length === 0) {
        return;
      }
    }

    // 设置射线
    this.raycaster.setFromCamera(
      this.currentMousePosition, 
      this.manager.sceneManager.activeCamera
    );

    // 检测与地形的交点
    const intersects = this.raycaster.intersectObjects(this.terrainMeshes, false);
    
    if (intersects.length > 0) {
      const hitPoint = intersects[0].point;
      const mesh = intersects[0].object;
      this.updateBrushIndicator(hitPoint);
    } else {
      this.hideBrushIndicator();
    }
  }


  /**
   * 鼠标抬起事件
   */
  onPointerUp(event) {
    if (!this.enabled) return;

    // 鼠标左键抬起时停止绘制
    if (event.button === 0) { // 0 表示鼠标左键
      this.isDrawing = false;

      if(this.modifyIntervalId){
        clearInterval(this.modifyIntervalId);
        this.modifyIntervalId=null;
      }
    }
  }

  /**
   * 更新鼠标位置
   */
  updateMousePosition(event) {
    const rect = this.manager.sceneManager.renderer.domElement.getBoundingClientRect();
    this.currentMousePosition.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
  }

  /**
   * 修改地形高度
   */
  modifyTerrain(event) {
    if (this.terrainMeshes.length === 0) {
      this.updateTerrainMeshes();
      if (this.terrainMeshes.length === 0) return;
    }

    // 设置射线
    this.raycaster.setFromCamera(
      this.currentMousePosition, 
      this.manager.sceneManager.activeCamera
    );

    // 检测与地形的交点
    const intersects = this.raycaster.intersectObjects(this.terrainMeshes, false);
    if (intersects.length === 0) return;

    const intersect = intersects[0];
    const hitPoint = intersect.point;

    const mesh = intersect.object;

    // 修改地形顶点
    this.modifyVertices(mesh, hitPoint);
  }

  /**
   * 修改顶点高度
   */
  modifyVertices(mesh, hitPoint) {
    const geometry = mesh.geometry;
    
    // 确保几何体有位置属性
    if (!geometry.attributes.position) {
      return;
    }

    const positions = geometry.attributes.position;
    const positionArray = positions.array;
    const vertex = new Vector3();
    let modified = false;

    // 获取世界变换矩阵
    const worldMatrix = mesh.matrixWorld;
    const inverseMatrix = worldMatrix.clone().invert();
    
    // 计算网格的缩放因子
    const scale = new Vector3();
    const position = new Vector3();
    const quaternion = mesh.quaternion.clone();
    worldMatrix.decompose(position, quaternion, scale);
    
    // 计算本地坐标系中的笔刷半径
    // 使用x和z轴的平均缩放因子
    const localRadius = this.radius / ((scale.x + scale.z) / 2);

    // 将命中点转换到本地坐标系
    const localHitPoint = hitPoint.clone().applyMatrix4(inverseMatrix);


    // 遍历所有顶点
    for (let i = 0; i < positionArray.length; i += 3) {    
      vertex.set(
        positionArray[i],
        positionArray[i + 1], 
        positionArray[i + 2]
      );

      // 计算顶点到命中点的距离（忽略Y轴）
      const distance = Math.sqrt(
        Math.pow(vertex.x - localHitPoint.x, 2) + 
        Math.pow(vertex.z - localHitPoint.z, 2)
      );

      // 使用本地坐标系中的半径进行比较
      if (distance <= localRadius) {

        // 计算归一化距离（相对于本地半径）
        const normalizedDistance = distance / localRadius;
        const falloff = this.calculateFalloff(normalizedDistance);
        const heightChange = this.strength * falloff * (this.invert ? -1 : 1);

        // 修改Y坐标（高度）
        positionArray[i + 1] += heightChange;
        modified = true;
        //break;
      }
    }

    // 如果有修改，更新几何体
    if (modified) {
      positions.needsUpdate = true;
      geometry.computeVertexNormals(); // 重新计算法线
      
      // 更新包围盒
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
    }
  }

  /**
   * 计算衰减值
   */
  calculateFalloff(normalizedDistance) {
    if (normalizedDistance >= 1) return 0;

    switch (this.falloffType) {
      case 'linear':
        return 1 - normalizedDistance;
      
      case 'gaussian':
        return Math.exp(-(normalizedDistance * normalizedDistance) * 3);
      
      default:
        return 1 - normalizedDistance;
    }
  }

  /**
   * 启用地形笔刷
   */
  enable() {
    console.log("启用地形笔刷");
    this.enabled = true;
    this.updateTerrainMeshes();
    
    // 保存所有地形的初始状态
    this.terrainMeshes.forEach(mesh => {
      this.saveOriginalState(mesh);
    });
    
    // 确保笔刷指示器已创建
    if (!this.brushIndicator) {
      console.log("重新创建笔刷指示器");
      this.createBrushIndicator();
    }
    
    // 如果指示器存在但不在场景中，重新添加
    if (this.brushIndicator && this.manager.sceneManager && this.manager.sceneManager.scene) {
      if (!this.manager.sceneManager.scene.getObjectByName("TerrainBrushIndicator")) {
        console.log("将笔刷指示器重新添加到场景");
        this.manager.sceneManager.scene.add(this.brushIndicator);
      }
    }
    
    console.log("地形笔刷已启用");
  }
  
  /**
   * 保存地形的初始状态
   * @param {Object} mesh 地形网格对象
   */
  saveOriginalState(mesh) {
    if (!mesh || !mesh.geometry || !mesh.geometry.attributes.position) return;
    
    // 检查是否已经保存了该网格的初始状态
    if (!this.originalTerrainStates.has(mesh.uuid)) {
      const positions = mesh.geometry.attributes.position.array;
      // 创建顶点位置数组的深拷贝
      const originalPositions = positions.slice();
      
      this.originalTerrainStates.set(mesh.uuid, {
        positions: originalPositions,
        mesh: mesh
      });
      
      console.log(`已保存地形 ${mesh.name || mesh.uuid} 的初始状态`);
    }
  }

  /**
   * 复原地形到初始状态
   * @param {Object} mesh 指定要复原的地形网格，如果不指定则复原所有已修改的地形
   * @returns {boolean} 是否成功复原
   */
  resetTerrain(mesh) {
    let restored = false;
    
    if (mesh) {
      // 复原指定地形
      const state = this.originalTerrainStates.get(mesh.uuid);
      if (state) {
        this.applyTerrainState(state);
        restored = true;
      }
    } else {
      // 复原所有地形
      this.originalTerrainStates.forEach(state => {
        this.applyTerrainState(state);
        restored = true;
      });
    }
    
    return restored;
  }

  /**
   * 应用保存的地形状态
   * @param {Object} state 包含mesh和positions的状态对象
   */
  applyTerrainState(state) {
    const { mesh, positions } = state;
    
    if (mesh && mesh.geometry && mesh.geometry.attributes.position) {
      const currentPositions = mesh.geometry.attributes.position.array;
      
      // 复制原始位置数据到当前位置
      for (let i = 0; i < positions.length; i++) {
        currentPositions[i] = positions[i];
      }
      
      // 更新几何体
      mesh.geometry.attributes.position.needsUpdate = true;
      mesh.geometry.computeVertexNormals();
      mesh.geometry.computeBoundingBox();
      mesh.geometry.computeBoundingSphere();
      
      console.log(`已复原地形 ${mesh.name || mesh.uuid} 到初始状态`);
    }
  }


  /**
   * 清理资源
   */
  dispose() {
    this.disable();
    
    // 清理可视化指示器
    if (this.brushIndicator) {
      if (this.manager.sceneManager && this.manager.sceneManager.scene) {
        this.manager.sceneManager.scene.remove(this.brushIndicator);
      }
      this.brushIndicator.geometry.dispose();
      this.brushIndicator = null;
    }
    
    if (this.indicatorMaterial) {
      this.indicatorMaterial.dispose();
      this.indicatorMaterial = null;
    }
    
    this.terrainMeshes = [];
    this.manager = null;

    if (this.modifyIntervalId) {
      clearInterval(this.modifyIntervalId);
      this.modifyIntervalId = null;
    }
  }

}